import { Link } from 'react-router-dom';
import type { Profile } from '@/types/db';
import { ProfileAvatar } from './profile-avatar';
import {
  calculateAgeVerbose,
  formatRelative,
} from '@/features/timeline/lib/formatDate';

function relationLabel(rel: Profile['relation'], relOther?: string | null): string {
  if (!rel) return '';
  if (rel === 'self') return 'You';
  if (rel === 'other') return relOther?.trim() || 'Other';
  return rel.charAt(0).toUpperCase() + rel.slice(1);
}


interface ProfileCardProps {
  profile: Profile;
  activeMedCount?: number;
  recordCount?: number;
  lastUpdated?: string | null;
}

export function ProfileCard({
  profile,
  activeMedCount,
  recordCount,
  lastUpdated,
}: ProfileCardProps) {
  const relativeUpdate = formatRelative(lastUpdated ?? null);
  const ageVerbose = calculateAgeVerbose(profile.date_of_birth);
  const relation = relationLabel(profile.relation, profile.relation_other) || 'Family member';
  const subline = ageVerbose ? `${relation} · ${ageVerbose}` : relation;

  return (
    <Link
      to={`/profile/${profile.id}`}
      className="card flex items-center gap-3 p-4 active:bg-canvas transition-colors min-h-tap"
    >
      <ProfileAvatar name={profile.name} color={profile.avatar_color} size="lg" />
      <div className="min-w-0 flex-1">
        <p className="font-bold truncate">{profile.name}</p>
        <p className="text-sm text-ink-muted truncate">{subline}</p>

        {/* Activity row */}
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          {activeMedCount !== undefined && activeMedCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#E6F4EA] text-[#2E7D32] text-xs font-medium">
              {activeMedCount === 1
                ? '1 active med'
                : `${activeMedCount} active meds`}
            </span>
          )}
          {recordCount !== undefined && recordCount > 0 && (
            <span className="text-xs text-ink-subtle">
              {recordCount === 1 ? '1 record' : `${recordCount} records`}
            </span>
          )}
          {relativeUpdate && (
            <span className="text-xs text-ink-subtle">
              · Updated {relativeUpdate}
            </span>
          )}
          {profile.allergies.length > 0 && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-flag-warn">
              ⚠ {profile.allergies.length === 1
                ? '1 allergy'
                : `${profile.allergies.length} allergies`}
            </span>
          )}
        </div>
      </div>
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="h-5 w-5 text-ink-subtle shrink-0"
        aria-hidden="true"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </Link>
  );
}
